<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import SideNavigation from "../components/SideNavigation.vue";
import AdminTopNavigation from "../components/admin/AdminTopNavigation.vue";
import { useAuthStore } from "@/store/auth.ts";
import { UiToaster } from "@shopexperts/shared";
import { useFeatureFlags } from "@/app/composables/useFeatureFlags";
import NotificationToastListener from "@/v2Projects/components/NotificationToastListener.vue";
import NotificationToastBody from "@/v2Projects/components/NotificationToastBody.vue";

import OverviewIcon from "../assets/icons/overview.svg";
import LeadsIcon from "../assets/icons/leads.svg";
import ListingIcon from "../assets/icons/listing.svg";
import QuoteIcon from "../assets/icons/quote-dark.svg";
import ReviewIcon from "../assets/icons/reviews.svg";
import TransactionIcon from "../assets/icons/transaction.svg";
import ReferralIcon from "../assets/icons/referral.svg";
import GridIcon from "../assets/icons/grid.svg";
import MessageIcon from "../assets/icons/message.svg";
import LogoutIcon from "../assets/icons/logout.svg";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { isEnabled } = useFeatureFlags();

const navItems = [
  { label: 'Expert Manager', icon: ListingIcon, path: '/admin/expert-manager', group: 'Experts & Leads' },
  { label: 'Expert Claims', icon: 'check-verified-02', path: '/admin/expert-claims', group: 'Experts & Leads' },
  { label: 'Expert Subscriptions', icon: TransactionIcon, path: '/admin/expert-subscriptions', group: 'Experts & Leads' },
  { label: 'Expert Levels', icon: TransactionIcon, path: '/admin/expert-levels', group: 'Experts & Leads' },
  { label: 'Wallet Transactions', icon: TransactionIcon, path: '/admin/wallet-transactions', group: 'Experts & Leads' },
  { label: 'Leads & Projects Manager', icon: LeadsIcon, path: '/admin/leads-projects', group: 'Experts & Leads' },
  { label: 'Reviews', icon: ReviewIcon, path: '/admin/reviews', group: 'Experts & Leads' },
  { label: 'Partner Directory', icon: 'search-md', path: '/admin/partner-directory', group: 'Experts & Leads' },
  { label: 'Trust Signals', icon: 'shield-tick', path: '/admin/confidence-signals', group: 'Experts & Leads' },

  { label: 'Categories Manager', icon: GridIcon, path: '/admin/categories-manager', group: 'Content' },
  { label: 'App & Theme Skills', icon: GridIcon, path: '/admin/apps-themes', group: 'Content' },
  { label: 'Pages', icon: GridIcon, path: '/admin/pages', group: 'Content' },
  { label: 'Redirects', icon: GridIcon, path: '/admin/redirects', group: 'Content' },
  { label: 'Customized Pages', icon: GridIcon, path: '/admin/customized-pages', group: 'Content' },
  { label: 'Automations', icon: 'settings-01', path: '/admin/automations', group: 'Content' },
  { label: 'Page Collections', icon: GridIcon, path: '/admin/page-collections', group: 'Content' },
  { label: 'Satellite Sites', icon: GridIcon, path: '/admin/satellite-sites', group: 'Content' },

  { label: 'Emails', icon: 'arrow-send', path: '/admin/emails', group: 'System' },
  { label: 'Ban List', icon: 'lock', path: '/admin/ban-list', group: 'System' },
  { label: 'AI Summaries', icon: 'star-06', path: '/admin/ai-summaries', group: 'System' },
  { label: 'Settings', icon: 'settings-01', path: '/admin/settings', group: 'System' },

  { label: 'Overview', icon: OverviewIcon, path: '/admin/dashboard', group: 'Legacy' },
  { label: 'Leads Manager', icon: LeadsIcon, path: '/admin/leads-manager', group: 'Legacy' },
  { label: 'Listings', icon: ListingIcon, path: '/admin/new-listings', group: 'Legacy' },
  { label: 'Clients', icon: LeadsIcon, path: '/admin/clients', group: 'Legacy' },
  { label: 'Quotes Sent', icon: QuoteIcon, path: '/admin/quotes-sent', group: 'Legacy' },
  { label: 'Transactions', icon: TransactionIcon, path: '/admin/transactions', group: 'Legacy' },
  { label: 'Referrals', icon: ReferralIcon, path: '/admin/referrals', group: 'Legacy' },
  { label: 'Email Preview', icon: MessageIcon, path: '/admin/email-preview', group: 'Legacy' },
]

const mobileMenuItems = [
  { label: 'Messages', icon: MessageIcon, path: '/admin/messages', badge: 4 },
]

const isMobileMenuOpen = ref(false);
const hideSideNavigationFromEvent = ref(false);
const hideSideNavigation = computed(() => hideSideNavigationFromEvent.value || route.meta.hideSideNavigation === true);

const handleSideNavigationVisibility = (event: Event) => {
  hideSideNavigationFromEvent.value = Boolean((event as CustomEvent<boolean>).detail);
}

const handleLogout = async () => {
  authStore.logout();
  await router.push('/login');
}

onMounted(() => {
  window.addEventListener('shopexperts:admin-sidebar-hidden', handleSideNavigationVisibility);
});

onBeforeUnmount(() => {
  window.removeEventListener('shopexperts:admin-sidebar-hidden', handleSideNavigationVisibility);
});

</script>

<template>
  <UiToaster v-if="isEnabled('enable_v2_projects')" placement="top-end" top-offset="4.75rem">
    <template #toast="{ toast }">
      <NotificationToastBody :toast="toast" />
    </template>
    <NotificationToastListener />
  </UiToaster>
  <div class="flex flex-col min-h-screen bg-muted">
      <AdminTopNavigation
          v-model:mobileMenuOpen="isMobileMenuOpen"
          profile-image="https://randomuser.me/api/portraits/men/32.jpg"
      />

      <div class="flex flex-1">
        <SideNavigation
            v-if="!hideSideNavigation"
            class="w-[15%]"
            :menu-items="navItems"
            :mobile-menu-items="mobileMenuItems"
            :mobile-logout-icon="LogoutIcon"
            :compact="true"
            v-model:mobileMenuOpen="isMobileMenuOpen"
            @logout="handleLogout"
        />

        <RouterView />
      </div>
  </div>
</template>

<style scoped>

</style>
